import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, assetUrl, BRAND_KEY } from "@/lib/api";
import { LanguageSelect } from "@/components/LanguageSelect";

export function AuthLayout({ children, title, subtitle }) {
  const { settings } = useAuth();
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="grain relative hidden flex-col justify-between overflow-hidden bg-[var(--brand-bg)] p-12 text-white lg:flex">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[hsl(var(--primary))] opacity-20 blur-3xl" />
        <div className="flex items-center gap-3">
          {settings?.logo_url ? (
            <img src={assetUrl(settings.logo_url)} alt="logo" className="h-10 w-10 rounded-md bg-white object-contain p-1" />
          ) : (
            <div className="font-display flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--primary))] text-xl font-extrabold">
              {(settings?.app_name || "N")[0]}
            </div>
          )}
          <div>
            <span className="font-display block text-xl font-bold" data-testid="auth-brand-name">{settings?.app_name}</span>
            {settings?.org_name && <span className="block text-xs text-white/60" data-testid="auth-brand-company">{settings.company_name || settings.org_name}</span>}
          </div>
        </div>
        <div className="relative max-w-md">
          <h1 className="text-4xl font-extrabold leading-tight xl:text-5xl">Il tuo team, in ordine.</h1>
          <p className="mt-5 text-base text-white/65">
            Ferie, permessi, malattie, comunicazioni e sondaggi aziendali in un unico spazio sicuro.
          </p>
        </div>
        <p className="flex items-center gap-2 text-xs text-white/40">
          <ShieldCheck className="h-4 w-4" /> Dati protetti · controlli di ruolo lato server
        </p>
      </div>
      <div className="relative flex items-center justify-center p-6 sm:p-12">
        <div className="absolute right-6 top-6"><LanguageSelect testId="login-language-select" /></div>
        <div className="w-full max-w-sm fade-up">
          {settings?.org_name && (
            <div className="mb-8 flex items-center gap-3 lg:hidden" data-testid="auth-brand-mobile">
              {settings.logo_url && <img src={assetUrl(settings.logo_url)} alt="logo" className="h-10 w-10 rounded-md border object-contain p-1" />}
              <span className="font-display text-lg font-bold">{settings.company_name || settings.org_name}</span>
            </div>
          )}
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="mt-1.5 mb-8 text-sm text-muted-foreground">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, signIn, settings, loadSettings } = useAuth();
  const { code } = useParams();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/setup/status").then((r) => r.data.needs_setup && nav("/setup", { replace: true }));
  }, [nav]);

  useEffect(() => {
    if (code) loadSettings(code);
  }, [code, loadSettings]);

  const brand = settings?.org_code || (code ? null : localStorage.getItem(BRAND_KEY));

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      signIn(data.user);
      nav("/", { replace: true });
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Accedi" subtitle={settings?.org_name ? `Area riservata di ${settings.company_name || settings.org_name}.` : "Inserisci le credenziali fornite dalla tua azienda."}>
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
        </div>
        {err && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">{err}</p>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Accedi
        </Button>
        <p className="text-sm">
          <Link to="/recupero-password" className="text-primary hover:underline" data-testid="forgot-password-link">Password dimenticata?</Link>
        </p>
        <div className="border-t pt-5">
          <p className="mb-2 text-sm text-muted-foreground">Sei nuovo?</p>
          <Button type="button" variant="outline" className="w-full" onClick={() => nav(brand ? `/registrati?codice=${brand}` : "/registrati")} data-testid="register-button">Registrati</Button>
        </div>
      </form>
    </AuthLayout>
  );
}
